import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl } from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader, SectionHeading, EmptyState } from "../components/Loader";
import { fmtDate } from "../lib/format";
import { FileText, Download, FolderOpen } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Documents() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const [cat, setCat] = useState("All");
  const { data: docs = [], isLoading: dl } = useQuery({
    queryKey: ["documents", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/documents`)).data, enabled: !!propertyId });

  const cats = useMemo(() => ["All", ...Array.from(new Set(docs.map((d) => d.category)))], [docs]);
  const filtered = docs.filter((d) => cat === "All" || d.category === cat);

  if (isLoading || dl) return <Loader label="Loading documents" />;

  return (
    <div className="space-y-6 arq-fade-up">
      <SectionHeading overline="Close" title="Document Center" />
      <div className="flex gap-2 flex-wrap">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`doc-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${cat === c ? "bg-arq-green text-white border-arq-green" : "bg-arq-card border-border text-arq-ink hover:border-arq-green"}`}>
            {c}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? <EmptyState icon={FolderOpen} title="No documents" subtitle="Documents shared by Arqitek will appear here." /> : (
        <div className="bg-arq-card border border-border rounded-md divide-y divide-border">
          {filtered.map((d) => (
            <div key={d.id} className="flex items-center gap-4 p-4" data-testid={`document-${d.id}`}>
              <div className="h-10 w-10 rounded-md bg-arq-surface flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-arq-green" strokeWidth={1.6} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-arq-ink truncate">{d.name}</p>
                <p className="text-xs text-muted-foreground">{d.category} · {fmtDate(d.created_at)}{d.description ? ` · ${d.description}` : ""}</p>
              </div>
              {d.file_id || d.url ? (
                <a href={d.url ? (d.url.startsWith("http") ? d.url : fileUrl(d.url)) : fileUrl(`/api/files/${d.file_id}`)} target="_blank" rel="noreferrer" data-testid={`download-${d.id}`}>
                  <Button variant="outline" size="sm"><Download className="h-4 w-4" />Open</Button>
                </a>
              ) : <span className="text-xs text-muted-foreground italic">Coming soon</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
